/**
 * FIELDWEAR — Product Page JavaScript
 * Target: Dawn theme assets/fieldwear-product.js
 *
 * Architecture:
 * - Hooks into Dawn's native variant system (does not replace it)
 * - Listens for Dawn's variant:change custom event
 * - Delegates ATC to Dawn's native <product-form> web component
 * - Manages gallery, UI state, scroll reveal, mobile CTA
 */

(function () {
  'use strict';

  /* ── Utilities ────────────────────────────────────────────── */

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.from((scope || document).querySelectorAll(selector));
  }

  function on(el, event, handler, options) {
    if (el) el.addEventListener(event, handler, options);
  }

  /* ── Gallery ──────────────────────────────────────────────── */

  class FWGallery {
    constructor(el) {
      this.el       = el;
      this.slides   = qsa('.fw-gallery__slide', el);
      this.thumbs   = qsa('.fw-gallery__thumb', el);
      this.dots     = qsa('.fw-gallery__dot', el);
      this.current  = 0;
      this.touchStartX = 0;

      this._bindThumbs();
      this._bindDots();
      this._bindSwipe();
    }

    goTo(index) {
      if (index < 0) index = this.slides.length - 1;
      if (index >= this.slides.length) index = 0;

      this.slides[this.current]?.classList.remove('is-active');
      this.thumbs[this.current]?.classList.remove('is-active');
      this.dots[this.current]?.classList.remove('is-active');

      this.current = index;

      this.slides[this.current]?.classList.add('is-active');
      this.thumbs[this.current]?.classList.add('is-active');
      this.dots[this.current]?.classList.add('is-active');
    }

    _bindThumbs() {
      this.thumbs.forEach((thumb, i) => {
        on(thumb, 'click', () => this.goTo(i));
        on(thumb, 'keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.goTo(i);
          }
        });
      });
    }

    _bindDots() {
      this.dots.forEach((dot, i) => {
        on(dot, 'click', () => this.goTo(i));
      });
    }

    _bindSwipe() {
      on(this.el, 'touchstart', (e) => {
        this.touchStartX = e.touches[0].clientX;
      }, { passive: true });

      on(this.el, 'touchend', (e) => {
        const dx = e.changedTouches[0].clientX - this.touchStartX;
        if (Math.abs(dx) > 40) {
          dx < 0 ? this.goTo(this.current + 1) : this.goTo(this.current - 1);
        }
      });
    }

    /**
     * Switch to a slide matching a given image src (partial match).
     * Called when variant changes to show the variant's image.
     */
    showImageBySrc(src) {
      if (!src) return;
      const match = this.slides.findIndex((slide) => {
        const img = qs('img', slide);
        return img && img.src && img.src.includes(src.split('?')[0].split('/').pop().split('.')[0]);
      });
      if (match !== -1) this.goTo(match);
    }
  }

  /* ── Hover Magnifier Lens (desktop, mouse-only) ──────────────── */

  class FWMagnifier {
    constructor(galleryEl) {
      this.stage = qs('.fw-gallery__main', galleryEl);
      this.lens  = qs('.fw-gallery__lens', galleryEl);
      this.zoom  = 1.8;
      this.size  = 200;
      if (!this.stage || !this.lens) return;
      this._bind();
    }

    _currentSrc() {
      const img = qs('.fw-gallery__slide.is-active img', this.stage);
      return img ? (img.dataset.zoomSrc || img.src) : null;
    }

    _bind() {
      on(this.stage, 'mouseenter', () => {
        const src = this._currentSrc();
        if (!src) return;
        this.lens.style.backgroundImage = 'url("' + src + '")';
        this.stage.classList.add('fw-gallery__main--zooming');
        this.lens.classList.add('fw-lens-active');
      });
      on(this.stage, 'mousemove', (e) => this._move(e));
      on(this.stage, 'mouseleave', () => {
        this.stage.classList.remove('fw-gallery__main--zooming');
        this.lens.classList.remove('fw-lens-active');
      });
    }

    _move(e) {
      const rect = this.stage.getBoundingClientRect();
      const half = this.size / 2;
      const x = Math.min(Math.max(e.clientX - rect.left, half), rect.width  - half);
      const y = Math.min(Math.max(e.clientY - rect.top,  half), rect.height - half);

      this.lens.style.left = (x - half) + 'px';
      this.lens.style.top  = (y - half) + 'px';
      this.lens.style.backgroundSize     = (rect.width * this.zoom) + 'px ' + (rect.height * this.zoom) + 'px';
      this.lens.style.backgroundPosition = '-' + (x * this.zoom - half) + 'px -' + (y * this.zoom - half) + 'px';
    }
  }

  /* ── Zoom Lightbox ────────────────────────────────────────── */

  class FWZoom {
    constructor(galleryEl) {
      this.gallery  = galleryEl;
      this.overlay  = null;
      this.stage    = null;
      this.img      = null;
      this.s        = { scale: 1, fit: 1, x: 0, y: 0, w: 0, h: 0 };
      this.pointers = new Map();
      this.pinchDist = 0;
      this.dragMoved = false;
      this.downTarget = null;

      this._bindOpeners();
    }

    _bindOpeners() {
      qsa('.fw-gallery__slide img', this.gallery).forEach((img) => {
        on(img, 'click', () => this.open(img.dataset.zoomSrc || img.src, img.alt));
      });
    }

    _build() {
      if (this.overlay) return;
      const el = document.createElement('div');
      el.className = 'fw-zoom-overlay';
      el.innerHTML =
        '<div class="fw-zoom-stage"><img class="fw-zoom-img" alt=""></div>' +
        '<button type="button" class="fw-zoom-close" aria-label="Close image zoom">&times;</button>' +
        '<div class="fw-zoom-hint">Scroll or pinch to zoom &middot; drag to move</div>';
      document.body.appendChild(el);
      this.overlay = el;
      this.stage   = qs('.fw-zoom-stage', el);
      this.img     = qs('.fw-zoom-img', el);

      on(qs('.fw-zoom-close', el), 'click', () => this.close());
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
      });
      this._bindStage();
    }

    open(src, alt) {
      this._build();
      this.img.alt = alt || '';
      this.img.onload = () => {
        this.s.w   = this.img.naturalWidth;
        this.s.h   = this.img.naturalHeight;
        this.s.fit = Math.min(this.stage.clientWidth / this.s.w, this.stage.clientHeight / this.s.h);
        this.setScale(this.s.fit);
      };
      this.img.src = src;
      this.overlay.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
    }

    close() {
      this.overlay.classList.remove('is-open');
      this.img.removeAttribute('src');
      this.pointers.clear();
      document.documentElement.style.overflow = '';
    }

    setScale(scale, cx, cy) {
      if (cx === undefined) { cx = this.stage.clientWidth / 2; cy = this.stage.clientHeight / 2; }
      const next = Math.min(Math.max(scale, this.s.fit), Math.max(this.s.fit * 6, 2));
      this.s.x = cx - (cx - this.s.x) * (next / this.s.scale);
      this.s.y = cy - (cy - this.s.y) * (next / this.s.scale);
      this.s.scale = next;
      this._apply();
    }

    _apply() {
      const sw = this.s.w * this.s.scale, sh = this.s.h * this.s.scale;
      const vw = this.stage.clientWidth,  vh = this.stage.clientHeight;
      this.s.x = sw <= vw ? (vw - sw) / 2 : Math.min(0, Math.max(vw - sw, this.s.x));
      this.s.y = sh <= vh ? (vh - sh) / 2 : Math.min(0, Math.max(vh - sh, this.s.y));
      this.img.style.transform = 'translate(' + this.s.x + 'px,' + this.s.y + 'px) scale(' + this.s.scale + ')';
    }

    _pinchInfo() {
      const pts = Array.from(this.pointers.values());
      const dx = pts[0].x - pts[1].x, dy = pts[0].y - pts[1].y;
      return {
        dist: Math.hypot(dx, dy),
        cx:   (pts[0].x + pts[1].x) / 2,
        cy:   (pts[0].y + pts[1].y) / 2,
      };
    }

    _bindStage() {
      const stage = this.stage;
      const rel = (e) => {
        const r = stage.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      };

      on(stage, 'wheel', (e) => {
        e.preventDefault();
        const p = rel(e);
        this.setScale(this.s.scale * Math.exp(-e.deltaY * 0.0025), p.x, p.y);
      }, { passive: false });

      on(stage, 'pointerdown', (e) => {
        stage.setPointerCapture(e.pointerId);
        this.pointers.set(e.pointerId, rel(e));
        this.dragMoved  = this.pointers.size > 1;
        this.downTarget = e.target;
        if (this.pointers.size === 2) this.pinchDist = this._pinchInfo().dist;
        stage.classList.add('is-dragging');
      });

      on(stage, 'pointermove', (e) => {
        if (!this.pointers.has(e.pointerId)) return;
        const prev = this.pointers.get(e.pointerId);
        const p    = rel(e);
        this.pointers.set(e.pointerId, p);

        if (this.pointers.size === 2) {
          const pinch = this._pinchInfo();
          if (this.pinchDist > 0) this.setScale(this.s.scale * (pinch.dist / this.pinchDist), pinch.cx, pinch.cy);
          this.pinchDist = pinch.dist;
        } else if (this.pointers.size === 1) {
          if (Math.abs(p.x - prev.x) + Math.abs(p.y - prev.y) > 3) this.dragMoved = true;
          this.s.x += p.x - prev.x;
          this.s.y += p.y - prev.y;
          this._apply();
        }
      });

      const release = (e) => {
        // pointer capture retargets pointerup to the stage, so backdrop-vs-image
        // must be decided from the target recorded at pointerdown
        const tapBackdrop = this.pointers.size === 1 && !this.dragMoved && this.downTarget === stage;
        this.pointers.delete(e.pointerId);
        this.pinchDist = 0;
        if (this.pointers.size === 0) stage.classList.remove('is-dragging');
        if (tapBackdrop) this.close();
      };
      on(stage, 'pointerup', release);
      on(stage, 'pointercancel', release);

      on(stage, 'dblclick', (e) => {
        const p = rel(e);
        const target = this.s.scale > this.s.fit * 1.5 ? this.s.fit : Math.max(1, this.s.fit * 2.5);
        this.setScale(target, p.x, p.y);
      });
    }
  }

  /* ── Color Swatches ───────────────────────────────────────── */

  class FWColorSelector {
    constructor(el, onChange) {
      this.el       = el;
      this.swatches = qsa('.fw-swatch', el);
      this.onChange = onChange;

      this._bind();
    }

    _bind() {
      this.swatches.forEach((swatch) => {
        on(swatch, 'click', () => this._select(swatch));
        on(swatch, 'keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._select(swatch);
          }
        });
      });
    }

    _select(swatch) {
      this.swatches.forEach((s) => {
        s.classList.remove('is-selected');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-selected');
      swatch.setAttribute('aria-pressed', 'true');

      const value = swatch.dataset.value;
      if (this.onChange) this.onChange(value);

      // Sync Dawn's native select element if present
      this._syncNativeSelect(value);
    }

    _syncNativeSelect(value) {
      // Dawn names selects "options[Color]" not "Color" — use contains selector
      const selects = qsa('select[name*="Color"], select[name*="color"]');
      selects.forEach((select) => {
        if (select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    setAvailability(availableValues) {
      this.swatches.forEach((swatch) => {
        const val = swatch.dataset.value;
        const unavailable = availableValues.length > 0 && !availableValues.includes(val);
        swatch.classList.toggle('is-unavailable', unavailable);
      });
    }
  }

  /* ── Size Buttons ─────────────────────────────────────────── */

  class FWSizeSelector {
    constructor(el, onChange) {
      this.el       = el;
      this.buttons  = qsa('.fw-size-btn', el);
      this.onChange = onChange;

      this._bind();
    }

    _bind() {
      this.buttons.forEach((btn) => {
        on(btn, 'click', () => {
          if (!btn.classList.contains('is-unavailable')) {
            this._select(btn);
          }
        });
        on(btn, 'keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!btn.classList.contains('is-unavailable')) this._select(btn);
          }
        });
      });
    }

    _select(btn) {
      this.buttons.forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');

      const value = btn.dataset.value;
      if (this.onChange) this.onChange(value);

      this._syncNativeSelect(value);
    }

    _syncNativeSelect(value) {
      const selects = qsa('select[name="Size"], select[name="size"]');
      selects.forEach((select) => {
        if (select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    setAvailability(unavailableValues) {
      this.buttons.forEach((btn) => {
        const val = btn.dataset.value;
        btn.classList.toggle('is-unavailable', unavailableValues.includes(val));
        btn.setAttribute('aria-disabled', unavailableValues.includes(val) ? 'true' : 'false');
      });
    }

    markSelected(value) {
      this.buttons.forEach((btn) => {
        const match = btn.dataset.value === value;
        btn.classList.toggle('is-selected', match);
        btn.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    }
  }

  /* ── Quantity Stepper ─────────────────────────────────────── */

  class FWQuantity {
    constructor(el) {
      this.el    = el;
      this.input = qs('.fw-quantity__input', el);
      this.dec   = qs('[data-action="decrement"]', el);
      this.inc   = qs('[data-action="increment"]', el);

      on(this.dec, 'click', () => this._adjust(-1));
      on(this.inc, 'click', () => this._adjust(1));
      on(this.input, 'change', () => this._clamp());
    }

    _adjust(delta) {
      const current = parseInt(this.input.value, 10) || 1;
      this.input.value = Math.max(1, current + delta);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _clamp() {
      const val = parseInt(this.input.value, 10);
      if (isNaN(val) || val < 1) this.input.value = 1;
    }

    get value() {
      return parseInt(this.input.value, 10) || 1;
    }
  }

  /* ── Design Cards ─────────────────────────────────────────── */

  class FWDesignSelector {
    constructor(el, onChange) {
      this.el      = el;
      this.cards   = qsa('.fw-design-card', el);
      this.onChange = onChange;
      this._bind();
    }

    _bind() {
      this.cards.forEach((card) => {
        on(card, 'click', () => this._select(card));
        on(card, 'keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._select(card);
          }
        });
      });
    }

    _select(card) {
      this.cards.forEach((c) => {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');

      const value = card.dataset.value;
      if (this.onChange) this.onChange(value);
      this._syncNativeSelect(value);
    }

    _syncNativeSelect(value) {
      const selects = qsa('select[name="Design"], select[name="design"]');
      selects.forEach((select) => {
        if (select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    markSelected(value) {
      this.cards.forEach((card) => {
        const match = card.dataset.value === value;
        card.classList.toggle('is-selected', match);
        card.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    }
  }

  /* ── Toast Notification ───────────────────────────────────── */

  function showToast(message, duration = 2800) {
    let toast = qs('.fw-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fw-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');

    clearTimeout(toast._timeout);
    toast._timeout = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, duration);
  }

  /* ── Size Guide Modal ─────────────────────────────────────── */

  class FWModal {
    constructor(el) {
      this.overlay = el;
      this.closeBtn = qs('.fw-modal__close', el);

      on(this.closeBtn, 'click', () => this.close());
      on(this.overlay, 'click', (e) => {
        if (e.target === this.overlay) this.close();
      });
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open() {
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      qs('.fw-modal', this.overlay)?.focus();
    }

    close() {
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      const video = qs('.fw-size-guide-video', this.overlay);
      if (video) video.pause();
    }
  }

  /* ── Accordion ────────────────────────────────────────────── */

  function initAccordion(el) {
    const triggers = qsa('.fw-accordion__trigger', el);
    triggers.forEach((trigger) => {
      on(trigger, 'click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const content = qs('.fw-accordion__content', trigger.closest('.fw-accordion__item'));

        trigger.setAttribute('aria-expanded', !expanded);
        content?.classList.toggle('is-open', !expanded);
      });
    });
  }

  /* ── Scroll Reveal ────────────────────────────────────────── */

  function initScrollReveal() {
    const els = qsa('.fw-reveal');
    if (!els.length) return;

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
      );
      els.forEach((el) => io.observe(el));
    } else {
      // Fallback: show all immediately
      els.forEach((el) => el.classList.add('is-visible'));
    }
  }

  /* ── Mobile Sticky CTA ────────────────────────────────────── */

  function initMobileCTA(productInfo) {
    const cta = qs('.fw-mobile-cta');
    const mainAtcBtn = qs('.fw-atc-btn:not(.fw-mobile-cta .fw-atc-btn)', productInfo || document);
    if (!cta || !mainAtcBtn) return;

    // Plain scroll-position check instead of IntersectionObserver — the observer-based
    // version could get stuck mid-transition when its callback fired in rapid
    // succession (toggling is-visible on/off faster than the CSS transition could
    // settle left the element's transform frozen at its hidden position, confirmed
    // live via getAnimations() showing progress stuck at 0 even under direct
    // override). rAF-throttled scroll listener is simpler and doesn't have that
    // failure mode.
    let ticking = false;
    function updateVisibility() {
      ticking = false;
      const btnBottom = mainAtcBtn.getBoundingClientRect().bottom;
      cta.classList.toggle('is-visible', btnBottom < 0);
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateVisibility);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    updateVisibility();
  }

  /* ── Dawn Variant Integration ─────────────────────────────── */
  /**
   * Dawn fires a custom 'variant:change' event on the document
   * when the selected variant changes. The event detail contains:
   *   { variant, sectionId, html }
   *
   * We use this to:
   * - Update price display
   * - Mark unavailable sizes
   * - Switch gallery image
   * - Enable/disable ATC button
   */

  function onVariantChange(event) {
    const { variant } = event.detail;
    if (!variant) return;

    updatePrice(variant);
    updateATCState(variant);

    // Gallery: switch to variant image if available
    if (variant.featured_image) {
      const gallery = window.__fwGallery;
      if (gallery) gallery.showImageBySrc(variant.featured_image.src);
    }
  }

  function updatePrice(variant) {
    const priceEl = qs('.fw-price');
    const compareEl = qs('.fw-price--compare');

    if (!priceEl) return;

    const formatter = new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
    });

    priceEl.textContent = formatter.format(variant.price / 100);

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatter.format(variant.compare_at_price / 100);
        compareEl.style.display = 'inline';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  function updateATCState(variant) {
    const btn = qs('.fw-atc-btn:not(.fw-mobile-cta .fw-atc-btn)');
    const mobileBtn = qs('.fw-mobile-cta .fw-atc-btn');
    const textEls = qsa('.fw-atc-text');

    if (!variant || !variant.available) {
      [btn, mobileBtn].forEach((b) => {
        if (!b) return;
        b.disabled = true;
        b.classList.add('fw-atc-btn--sold-out');
      });
      textEls.forEach((t) => (t.textContent = 'Sold Out'));
    } else {
      [btn, mobileBtn].forEach((b) => {
        if (!b) return;
        b.disabled = false;
        b.classList.remove('fw-atc-btn--sold-out');
      });
      textEls.forEach((t) => (t.textContent = 'Add to Cart'));
    }
  }

  /* ── ATC Button — delegates to Dawn's product-form ───────── */
  /**
   * Dawn's <product-form> web component handles the actual cart mutation.
   * We overlay our styled button on top and trigger Dawn's hidden submit button,
   * or dispatch a submit event on Dawn's form, then show our loading/toast UI.
   */

  function initATCButton(btn) {
    if (!btn) return;

    on(btn, 'click', async (e) => {
      e.preventDefault();
      if (btn.disabled || btn.classList.contains('fw-atc-btn--loading')) return;

      // Find Dawn's product-form element and its submit button
      const productForm = qs('product-form');
      const dawnSubmit  = qs('[type="submit"]', productForm);

      if (!productForm || !dawnSubmit) {
        // Fallback: show error toast
        showToast('Unable to add — please refresh and try again.');
        return;
      }

      // Show loading state
      btn.classList.add('fw-atc-btn--loading');
      btn.disabled = true;

      // Also update the mobile CTA button
      const mobileBtn = qs('.fw-mobile-cta .fw-atc-btn');
      if (mobileBtn) {
        mobileBtn.classList.add('fw-atc-btn--loading');
        mobileBtn.disabled = true;
      }

      // Delegate to Dawn — click Dawn's hidden submit
      dawnSubmit.click();

      // Listen for Dawn's cart update confirmation
      // Dawn dispatches 'cart:refresh' or updates the cart icon
      const done = () => {
        btn.classList.remove('fw-atc-btn--loading');
        btn.disabled = false;
        if (mobileBtn) {
          mobileBtn.classList.remove('fw-atc-btn--loading');
          mobileBtn.disabled = false;
        }
        showToast('Added to cart ✓');
      };

      // Dawn fires a custom event on the cart drawer/notification
      // We listen for it or fall back to a timeout
      const cartRefreshHandler = () => {
        done();
        document.removeEventListener('cart:refresh', cartRefreshHandler);
        document.removeEventListener('cart:updated', cartRefreshHandler);
      };

      document.addEventListener('cart:refresh', cartRefreshHandler, { once: true });
      document.addEventListener('cart:updated', cartRefreshHandler, { once: true });

      // Fallback timeout in case Dawn's event doesn't fire
      setTimeout(() => {
        cartRefreshHandler();
      }, 3000);
    });
  }

  /* ── Main Init ────────────────────────────────────────────── */

  function init() {
    const productPage = qs('.fw-product-page');
    if (!productPage) return;

    const sectionId   = productPage.dataset.sectionId;
    const productData = window.__fwProductData?.[sectionId];
    const currentVariant = productData?.currentVariant;

    // Gallery
    const galleryEl = qs('.fw-gallery', productPage);
    if (galleryEl) {
      window.__fwGallery = new FWGallery(galleryEl);
      window.__fwGallery.goTo(0);
      window.__fwZoom = new FWZoom(galleryEl);
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        window.__fwMagnifier = new FWMagnifier(galleryEl);
      }
    }

    // Design selector
    const designEl = qs('[data-fw-option="Design"]', productPage);
    if (designEl) {
      const designSelector = new FWDesignSelector(designEl, (value) => {
        console.log('[FW] Design selected:', value);
      });
      window.__fwDesignSelector = designSelector;

      // Sync initial state from current variant
      if (currentVariant) {
        const designOptionIndex = getOptionIndex(productData, 'Design');
        if (designOptionIndex !== -1) {
          designSelector.markSelected(currentVariant.options[designOptionIndex]);
        }
      }
    }

    // Color selector — init with current variant's color pre-selected
    const colorEl = qs('[data-fw-option="Color"]', productPage);
    if (colorEl) {
      const colorSelector = new FWColorSelector(colorEl, (value) => {
        // Update the color label
        const label = qs('#fw-color-label-' + colorEl.dataset.optionIndex, productPage);
        if (label) label.textContent = '— ' + value;
        // Update size availability for this color
        if (productData) syncSizeAvailability(value, productData.variants, sizeSelector);
        // Switch gallery to the first variant with this color that has a featured image
        if (productData && window.__fwGallery) {
          const colorIdx = getOptionIndex(productData, 'Color');
          if (colorIdx !== -1) {
            const match = productData.variants.find(v => v.options[colorIdx] === value && v.featured_image);
            if (match) window.__fwGallery.showImageBySrc(match.featured_image.src);
          }
        }
      });
      window.__fwColorSelector = colorSelector;

      // Sync initial selected state from current variant
      if (currentVariant) {
        const colorOptionIndex = getOptionIndex(productData, 'Color');
        if (colorOptionIndex !== -1) {
          const currentColor = currentVariant.options[colorOptionIndex];
          const matchingSwatch = qs(`.fw-swatch[data-value="${CSS.escape(currentColor)}"]`, colorEl);
          if (matchingSwatch) {
            qsa('.fw-swatch', colorEl).forEach(s => {
              s.classList.remove('is-selected');
              s.setAttribute('aria-pressed', 'false');
            });
            matchingSwatch.classList.add('is-selected');
            matchingSwatch.setAttribute('aria-pressed', 'true');
          }
        }
      }
    }

    // Size selector — init with current variant's size pre-selected
    const sizeEl = qs('[data-fw-option="Size"]', productPage);
    var sizeSelector = null;
    if (sizeEl) {
      sizeSelector = new FWSizeSelector(sizeEl, (value) => {
        // Update hidden qty input to sync with Dawn's form
        const qtyHidden = qs('#fw-qty-hidden-' + sectionId);
        if (qtyHidden && window.__fwQuantity) {
          qtyHidden.value = window.__fwQuantity.value;
        }
      });
      window.__fwSizeSelector = sizeSelector;

      // Sync initial selected state
      if (currentVariant) {
        const sizeOptionIndex = getOptionIndex(productData, 'Size');
        if (sizeOptionIndex !== -1) {
          const currentSize = currentVariant.options[sizeOptionIndex];
          sizeSelector.markSelected(currentSize);
        }
      }

      // Set unavailable sizes based on current color selection
      if (productData && currentVariant) {
        const colorOptionIndex = getOptionIndex(productData, 'Color');
        const currentColor = colorOptionIndex !== -1 ? currentVariant.options[colorOptionIndex] : null;
        if (currentColor) syncSizeAvailability(currentColor, productData.variants, sizeSelector);
      }
    }

    // Quantity stepper
    const quantityEl = qs('.fw-quantity', productPage);
    if (quantityEl) {
      window.__fwQuantity = new FWQuantity(quantityEl);
    }

    // Size guide modal
    const modalOverlay = qs('.fw-modal-overlay');
    if (modalOverlay) {
      const modal = new FWModal(modalOverlay);
      const sizeGuideBtn = qs('.fw-size-guide-btn', productPage);
      on(sizeGuideBtn, 'click', () => modal.open());
    }

    // Accordion
    const accordionEl = qs('.fw-accordion', productPage);
    if (accordionEl) initAccordion(accordionEl);

    // ATC buttons
    const atcBtn       = qs('.fw-atc-btn:not(.fw-mobile-cta .fw-atc-btn)', productPage);
    const mobileAtcBtn = qs('.fw-mobile-cta .fw-atc-btn');
    initATCButton(atcBtn);
    initATCButton(mobileAtcBtn);

    // Set initial ATC state from current variant
    if (currentVariant) updateATCState(currentVariant);

    // Mobile sticky CTA
    const productInfo = qs('.fw-product-info', productPage);
    initMobileCTA(productInfo);

    // Scroll reveal
    initScrollReveal();

    // Dawn variant change hook — also update our selectors
    document.addEventListener('variant:change', (e) => {
      onVariantChange(e);
      const pd = window.__fwProductData?.[sectionId];
      if (!pd || !e.detail?.variant) return;
      // Sync size
      if (window.__fwSizeSelector) {
        const si = getOptionIndex(pd, 'Size');
        if (si !== -1) window.__fwSizeSelector.markSelected(e.detail.variant.options[si]);
      }
      // Sync design
      if (window.__fwDesignSelector) {
        const di = getOptionIndex(pd, 'Design');
        if (di !== -1) window.__fwDesignSelector.markSelected(e.detail.variant.options[di]);
      }
    });
  }

  /* ── Helpers ──────────────────────────────────────────────── */

  function getOptionIndex(productData, optionName) {
    if (!productData?.options) return -1;
    const lower = optionName.toLowerCase();
    return productData.options.findIndex(o => o.toLowerCase() === lower);
  }

  function syncSizeAvailability(selectedColor, variants, sizeSelector) {
    if (!sizeSelector || !variants) return;
    // Find which sizes are unavailable for this color
    const unavailable = [];
    const allSizes = [...new Set(variants.flatMap(v => v.options))];
    allSizes.forEach(size => {
      const hasAvailable = variants.some(v =>
        v.options.includes(selectedColor) &&
        v.options.includes(size) &&
        v.available
      );
      if (!hasAvailable && sizeSelector.buttons.some(b => b.dataset.value === size)) {
        unavailable.push(size);
      }
    });
    sizeSelector.setAvailability(unavailable);
  }

  /* ── Boot ─────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

